import type { ApexOptions } from 'apexcharts';
import { useEffect, useRef, useState } from 'react';
import Chart from 'react-apexcharts';
import { useTranslation } from 'react-i18next';
import { type ColumnDefinition, ReactTabulator } from 'react-tabulator';
import type { CellComponent } from 'tabulator-tables';
import type { LaneCountsByTime, LaneTrafficSummary } from '@/shared/types/types-statistic';
import { useColorStore } from '@/store/colorStore';
import { useThemeStore } from '@/store/themeStore';
import { lttbIndices } from '../utils/lttb';

const statisticByTypeTabulator = [
  {
    title: 'No',
    formatter: (cell: CellComponent) => {
      const row = cell.getRow();
      const page = row.getTable().getPage();
      const pageSize = row.getTable().getPageSize();
      return (page - 1) * pageSize + row.getPosition(true);
    },
    width: 60,
    hozAlign: 'center',
    headerHozAlign: 'center',
    headerSort: false,
    resizable: false,
  },
  {
    title: '집계시간',
    field: 'aggregated_time',
    hozAlign: 'center',
    widthGrow: 1,
    headerHozAlign: 'center',
    headerSort: false,
    resizable: false,
  },
  {
    title: '사이트',
    field: 'site_name',
    widthGrow: 1,
    hozAlign: 'center',
    headerHozAlign: 'center',
    headerSort: false,
    resizable: false,
  },
  {
    title: '접근로',
    field: 'road_name',
    widthGrow: 1,
    hozAlign: 'center',
    headerHozAlign: 'center',
    headerSort: false,
    resizable: false,
  },
  {
    title: '차로 전체',
    field: 'lane_all',
    width: 70,
    hozAlign: 'center',
    headerHozAlign: 'center',
    headerSort: false,
    resizable: false,
    formatter: (cell: CellComponent) => {
      const value = cell.getValue();
      return value === null || value === undefined ? '-' : value;
    },
  },
  {
    title: '1차로',
    field: 'lane_1',
    width: 70,
    hozAlign: 'center',
    headerHozAlign: 'center',
    headerSort: false,
    resizable: false,
    formatter: (cell: CellComponent) => {
      const value = cell.getValue();
      return value === null || value === undefined ? '-' : value;
    },
  },
  {
    title: '2차로',
    field: 'lane_2',
    width: 70,
    hozAlign: 'center',
    headerHozAlign: 'center',
    headerSort: false,
    resizable: false,
    formatter: (cell: CellComponent) => {
      const value = cell.getValue();
      return value === null || value === undefined ? '-' : value;
    },
  },
  {
    title: '3차로',
    field: 'lane_3',
    width: 70,
    hozAlign: 'center',
    headerHozAlign: 'center',
    headerSort: false,
    resizable: false,
    formatter: (cell: CellComponent) => {
      const value = cell.getValue();
      return value === null || value === undefined ? '-' : value;
    },
  },
  {
    title: '4차로',
    field: 'lane_4',
    width: 70,
    hozAlign: 'center',
    headerHozAlign: 'center',
    headerSort: false,
    resizable: false,
    formatter: (cell: CellComponent) => {
      const value = cell.getValue();
      return value === null || value === undefined ? '-' : value;
    },
  },
  {
    title: '5차로',
    field: 'lane_5',
    width: 70,
    hozAlign: 'center',
    headerHozAlign: 'center',
    headerSort: false,
    resizable: false,
    formatter: (cell: CellComponent) => {
      const value = cell.getValue();
      return value === null || value === undefined ? '-' : value;
    },
  },
];

const StatisticByLane = ({
  data,
  chartData,
}: {
  data?: LaneTrafficSummary[];
  chartData?: LaneCountsByTime[];
}) => {
  const { t } = useTranslation();
  const flatData = data?.flat(Infinity);
  const dataCntLane = flatData?.map((item) => {
    const {
      aggregate_end_time,
      aggregate_start_time,
      aggregated_time,
      lane_all,
      lane_1,
      lane_2,
      lane_3,
      lane_4,
      lane_5,
      road_id,
      road_name,
      site_id,
      site_name,
    } = item;

    return {
      aggregate_end_time,
      aggregate_start_time,
      aggregated_time,
      lane_all,
      lane_1,
      lane_2,
      lane_3,
      lane_4,
      lane_5,
      road_id,
      road_name,
      site_id,
      site_name,
    };
  });

  const chartRecords = chartData as unknown as Record<string, unknown>[] | undefined;
  const dataCntLaneChart: Record<string, (number | string)[]> | null = chartRecords?.length
    ? (Object.fromEntries(
        Object.keys(chartRecords[0] ?? {}).map((key) => [
          key,
          chartRecords.map((item) => item[key] ?? 0),
        ])
      ) as Record<string, (number | string)[]>)
    : null;

  const languageTabulator = () => {
    const datalanguage = {
      pagination: {
        first: t('cmn > first page'), //text for the first page button
        first_title: t('cmn > first page'), //tooltip text for the first page button
        last: t('cmn > last page'),
        last_title: t('cmn > last page'),
        prev: t('cmn > page before'),
        prev_title: t('cmn > page before'),
        next: t('cmn > next page'),
        next_title: t('cmn > next page'),
      },
    };
    return datalanguage;
  };

  const optionsTabulator = {
    pagination: true,
    paginationSize: 10,
    rowHeight: 41,
    movableRows: false,
    index: 'id',
    locale: 'ko',
    langs: {
      ko: languageTabulator(),
    },
    resizableRows: false,
    footerElement: `<div style="padding: 0 20px 0 0; text-align: right;">총 ${dataCntLane?.length || 0} 건</div>`,
    placeholder: "<div style='text-align:center;'> No Data</div>",
  };

  const { apexChart: apexColors, palette } = useColorStore();
  // Theme is read here so the tooltip follows the current light/dark mode.
  const isDark = useThemeStore((state) => state.isDark);

  // Downsample to the chart's pixel width. All series share one x-axis, so pick
  // the kept buckets once (driven by lane_all) and slice every series by them.
  const cellRef = useRef<HTMLDivElement>(null);
  const [chartWidth, setChartWidth] = useState(0);
  useEffect(() => {
    const el = cellRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width ?? 0;
      setChartWidth(Math.round(w / 50) * 50); // 50px steps to limit re-renders
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const pointBudget = chartWidth > 0 ? Math.max(100, chartWidth) : 1000;

  const times = (dataCntLaneChart?.aggregate_start_time ?? []) as (string | number)[];
  const xs = times.map((t) => new Date(t as string).getTime());
  const keepIdx = lttbIndices(
    xs,
    (dataCntLaneChart?.lane_all ?? []).map((v) => Number(v) || 0),
    pointBudget
  );
  const pickSeries = (key: string) => {
    const vals = dataCntLaneChart?.[key] ?? [];
    return keepIdx.map((i) => ({ x: xs[i] as number, y: Number(vals[i] ?? 0) }));
  };

  const options = {
    chart: {
      type: 'line',
      zoom: {
        enabled: false,
      },
      fontFamily: 'Pretendard GOV',
      toolbar: {
        show: false,
      },
      foreColor: apexColors.foreColor,
    },
    // Slot 0 = "전체" (aggregate total, neutral), slots 1..5 = 1–5차로.
    colors: [apexColors.seriesTotal, ...palette.category.slice(0, 5)],
    markers: {
      size: 4,
    },
    stroke: {
      show: true,
      curve: 'straight',
      width: 1.5,
    },
    grid: {
      borderColor: apexColors.gridBorderColor,
    },
    xaxis: {
      type: 'datetime',
      // Bucket timestamps are UTC ('...Z'); render their wall-clock as-is.
      labels: { rotate: 0, datetimeUTC: true },
    },
    tooltip: {
      theme: isDark ? 'dark' : 'light',
      x: { format: 'yyyy-MM-dd HH:mm' },
    },
  };

  const averageWaitTime = [
    { name: '전체', data: pickSeries('lane_all') },
    { name: '1차로', data: pickSeries('lane_1') },
    { name: '2차로', data: pickSeries('lane_2') },
    { name: '3차로', data: pickSeries('lane_3') },
    { name: '4차로', data: pickSeries('lane_4') },
    { name: '5차로', data: pickSeries('lane_5') },
  ] as ApexAxisChartSeries;

  return (
    <section>
      <ReactTabulator
        data={dataCntLane}
        columns={statisticByTypeTabulator as unknown as ColumnDefinition[]}
        layout={'fitColumns'}
        className="tabulator-custom w-full "
        //   pagination="local"
        options={optionsTabulator}
      />
      <div
        ref={cellRef}
        className="mt-[40px] flex flex-col border-[3px] rounded-[3px] p-[10px]"
        style={{ borderColor: 'var(--chart-border)' }}
      >
        <span className="title3bold" style={{ color: 'var(--chart-label)' }}>
          차로별 통행량
        </span>
        <Chart
          type="line"
          options={options as ApexOptions}
          series={averageWaitTime}
          height={350}
          className={'flex w-full !min-h-[0]'}
        />
      </div>
    </section>
  );
};

export default StatisticByLane;
